import { Badge, Button, Group, Stack, Table, Text, Title } from '@mantine/core';
import { useState } from 'react';
import PageLoader from 'src/UI/loader/PageLoader';
import {
  type MoneyhubAccount,
  useMoneyhubAccounts,
  useMoneyhubTransactions,
  useStartMoneyhubConnection,
} from './useMoneyhubBanking';

function formatBalance(minor: number | null, currency: string | null): string {
  if (minor === null || minor === undefined) return '—';
  const value = minor / 100;
  return currency ? `${currency} ${value.toFixed(2)}` : value.toFixed(2);
}

function TransactionsPanel({ accountId }: { accountId: string }) {
  const { data: transactions, isLoading, isError } = useMoneyhubTransactions(accountId);

  if (isLoading) return <PageLoader />;
  if (isError) return <Text c="red">Failed to load transactions</Text>;
  if (!transactions?.length) return <Text c="dimmed">No transactions found</Text>;

  return (
    <Table striped withTableBorder fz="xs">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Date</Table.Th>
          <Table.Th>Description</Table.Th>
          <Table.Th>Merchant</Table.Th>
          <Table.Th>Amount</Table.Th>
          <Table.Th>Status</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {transactions.map(tx => (
          <Table.Tr key={tx.id}>
            <Table.Td>{tx.booking_date}</Table.Td>
            <Table.Td>{tx.description ?? '—'}</Table.Td>
            <Table.Td>{tx.merchant_name ?? '—'}</Table.Td>
            <Table.Td>{formatBalance(tx.amount_minor, tx.currency)}</Table.Td>
            <Table.Td>{tx.status && <Badge variant="light">{tx.status}</Badge>}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

function AccountRow({
  account,
  isSelected,
  onSelect,
}: {
  account: MoneyhubAccount;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <>
      <Table.Tr
        style={{
          cursor: 'pointer',
          background: isSelected ? 'var(--mantine-color-blue-0)' : undefined,
        }}
      >
        <Table.Td>{account.name ?? '—'}</Table.Td>
        <Table.Td>{account.provider_name ?? '—'}</Table.Td>
        <Table.Td>{account.type}</Table.Td>
        <Table.Td>
          <Badge variant="light" color={account.status === 'active' ? 'green' : 'gray'}>
            {account.status}
          </Badge>
        </Table.Td>
        <Table.Td>{formatBalance(account.current_balance_minor, account.currency)}</Table.Td>
        <Table.Td>
          <Button size="xs" variant={isSelected ? 'filled' : 'light'} onClick={onSelect}>
            {isSelected ? 'Hide transactions' : 'View transactions'}
          </Button>
        </Table.Td>
      </Table.Tr>
      {isSelected && (
        <Table.Tr>
          <Table.Td colSpan={6}>
            <TransactionsPanel accountId={account.id} />
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
}

export default function OpenBankingTab() {
  const { data: accounts, isLoading, isError, refetch, isRefetching } = useMoneyhubAccounts();
  const { startConnection, isStarting, startError } = useStartMoneyhubConnection();
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);

  const handleConnect = async () => {
    const result = await startConnection({ bank_id: null });
    sessionStorage.setItem('mh_connection_pending', '1');
    window.location.href = result.data.auth_url;
  };

  const toggleAccount = (accountId: string) => {
    setSelectedAccountId(prev => (prev === accountId ? null : accountId));
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={3}>Open banking accounts</Title>
        <Group gap="sm">
          <Button variant="light" loading={isRefetching} onClick={() => refetch()}>
            Refresh
          </Button>
          <Button loading={isStarting} onClick={handleConnect}>
            Connect bank account
          </Button>
        </Group>
      </Group>

      {startError && <Text c="red">Failed to start connection: {startError.message}</Text>}

      {isLoading && <PageLoader />}

      {isError && <Text c="red">Failed to load accounts</Text>}

      {!isLoading && !isError && (!accounts || accounts.length === 0) && (
        <Text c="dimmed">
          {'No bank accounts connected yet. Click "Connect bank account" to get started.'}
        </Text>
      )}

      {accounts && accounts.length > 0 && (
        <Table withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Provider</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Balance</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {accounts.map(account => (
              <AccountRow
                key={account.id}
                account={account}
                isSelected={selectedAccountId === account.id}
                onSelect={() => toggleAccount(account.id)}
              />
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}
